import React, { useState } from "react";

import { CalendarRange } from "lucide-react";

import { Button, ButtonShadcn } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";

// Shared analytics date-range control. There is no dedicated range picker in the
// kit, so this composes quick presets (the common case) with two DatePickers for
// a custom window (the Filter `type:"row"` idiom). Emits { from, to } as
// YYYY-MM-DD (either may be "" → unbounded that side).

const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const presetRange = (days) => {
  if (days === null) return { from: "", to: "" }; // All time
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: toYmd(from), to: toYmd(to) };
};

const PRESETS = [
  { key: "7", label: "7d", days: 7 },
  { key: "30", label: "30d", days: 30 },
  { key: "90", label: "90d", days: 90 },
  { key: "all", label: "All", days: null },
];

const DateRangeBar = ({ value, onChange, activePreset, onPresetChange }) => {
  const [custom, setCustom] = useState(false);

  const applyPreset = (p) => {
    setCustom(false);
    onPresetChange?.(p.key);
    onChange?.(presetRange(p.days));
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1.5 text-[12px] text-text-secondary mr-1">
        <CalendarRange size={14} /> Period
      </span>

      <div className="flex items-center gap-1 rounded-lg border border-border-primary p-0.5 bg-background-secondary">
        {PRESETS.map((p) => {
          const active = !custom && activePreset === p.key;
          return (
            <Button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              className={
                "px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors " +
                (active
                  ? "bg-button-primary text-text-invert"
                  : "text-text-secondary hover:bg-background-tertiary")
              }
            >
              {p.label}
            </Button>
          );
        })}
        <Button
          type="button"
          onClick={() => {
            setCustom((c) => !c);
            onPresetChange?.("custom");
          }}
          className={
            "px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors " +
            (custom
              ? "bg-button-primary text-text-invert"
              : "text-text-secondary hover:bg-background-tertiary")
          }
        >
          Custom
        </Button>
      </div>

      {custom ? (
        <div className="flex items-center gap-2">
          <div className="w-[150px]">
            <DatePicker
              value={value?.from || ""}
              onChange={(v) => onChange?.({ ...value, from: v })}
              placeholder="From"
              maxDate={value?.to || undefined}
              buttonClassName="h-9"
            />
          </div>
          <span className="text-text-secondary text-[12px]">→</span>
          <div className="w-[150px]">
            <DatePicker
              value={value?.to || ""}
              onChange={(v) => onChange?.({ ...value, to: v })}
              placeholder="To"
              minDate={value?.from || undefined}
              buttonClassName="h-9"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DateRangeBar;
